import {
  FunctionComponent,
  useCallback,
  useEffect,
  useState,
  useRef,
} from "react";
import Column from "../../../../components/Column";
import Text from "../../../../components/Text";
import styled from "styled-components";
import { styles } from "../../../../styles/themes/style";
import { useFeedsHandlers } from "../../../../services/actions/feeds/useFeedsHandlers";
import { useFeedsStore } from "../../../../stores/feeds/useFeedsStore";
import { useAppHandlers } from "../../../../services/actions/app/useAppHandlers";
import { IFeed } from "../../../../interfaces/models/feed";
import { IConnection } from "../../../../interfaces/models/connection";
import Checkbox from "../../../../components/Checkbox";
import ProfilePhoto from "../../../../components/ProfilePhoto";
import { capitalLetters } from "../../../../utils";
import Button from "../../../../components/Button";
import Table from "../../../../components/Table";
import useMediaQuery from "../../../../components/useMediaQuery";
import EditDeleteActionDropdown from "../../../../fragments/dropdowns/EditDeleteActionDropdown";
import ConnectionActionWithFeedsDropdown from "../../../../fragments/dropdowns/ConnectionActionWithFeedsDropdown";
import { adjustedColumnsHandler } from "../../../../utils";
import EmptyPage from "../../../../components/EmptyPage";
import ActionText from "../../../../components/ActionText";
import Row from "../../../../components/Row";
import SelectAllCheckbox from "../../../../components/SelectAllCheckbox";
import SvgFragment from "../../../../fragments/SvgFragment";

interface IManageFeedsProps {
  onBack: () => void;
}

const ManageFeeds: FunctionComponent<IManageFeedsProps> = ({ onBack }) => {
  const {
    getFeedsHandler,
    deleteFeedHandler,
    renameFeedHandler,
    getFeedConnectionsHandler,
    bulkRemoveConnectionsFromFeedHandler,
  } = useFeedsHandlers();
  const {
    setDestructiveModal,
    hideModal,
    setRenameFeedModal,
    setCreateFeedModal,
  } = useAppHandlers();
  const {
    store: { feeds, loading },
  } = useFeedsStore();
  const tableRef = useRef<HTMLDivElement>(null);
  const isSmall = useMediaQuery("(max-width: 768px)");
  const isMedium = useMediaQuery("(max-width: 1240px)");
  const [showConnections, setShowConnections] = useState<boolean>(false);
  const [selectedFeed, setSelectedFeed] = useState<IFeed | null>(null);
  const [feedConnections, setFeedConnections] = useState<IConnection[]>([]);
  const [connectionsLoading, setConnectionsLoading] = useState<boolean>(false);
  const [selectedConnections, setSelectedConnections] = useState<string[]>([]);
  const [isAllSelected, setIsAllSelected] = useState<boolean>(false);

  useEffect(() => {
    const fetchFeeds = async () => {
      try {
        await getFeedsHandler();
      } catch (error) {
        console.error("Error fetching feeds:", error);
      }
    };
    fetchFeeds();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Initialize state from localStorage
  useEffect(() => {
    const showConnectionsParam =
      localStorage.getItem("postsSection_showConnections") === "true";
    const selectedFeedId = localStorage.getItem("postsSection_selectedFeedId");

    if (showConnectionsParam && selectedFeedId && feeds.length > 0) {
      const feed = feeds.find((f: IFeed) => f._id === selectedFeedId);
      if (feed) {
        setSelectedFeed(feed);
        setShowConnections(true);
        // Load connections for the selected feed
        setConnectionsLoading(true);
        getFeedConnectionsHandler(feed._id)
          .then((result) => {
            setFeedConnections(result?.connections || []);
            setConnectionsLoading(false);
          })
          .catch((error) => {
            console.error("Error fetching feed connections:", error);
            setConnectionsLoading(false);
          });
      }
    }
  }, [feeds]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleRename = useCallback(
    (feed: IFeed) => {
      setRenameFeedModal(feed.name, async (newName: string) => {
        try {
          await renameFeedHandler(feed._id, newName);
        } catch (error) {
          console.error("Error renaming feed:", error);
        }
      });
    },
    [setRenameFeedModal, renameFeedHandler]
  );

  const handleCreateFeed = useCallback(() => {
    setCreateFeedModal([], true);
  }, [setCreateFeedModal]);

  const handleFeedClick = useCallback(
    async (feed: IFeed) => {
      setSelectedFeed(feed);
      setConnectionsLoading(true);
      setShowConnections(true);
      setSelectedConnections([]);
      setIsAllSelected(false);

      // Save to localStorage
      localStorage.setItem("postsSection_showConnections", "true");
      localStorage.setItem("postsSection_selectedFeedId", feed._id);

      try {
        const result = await getFeedConnectionsHandler(feed._id);
        setFeedConnections(result?.connections || []);
      } catch (error) {
        console.error("Error fetching feed connections:", error);
      } finally {
        setConnectionsLoading(false);
      }
    },
    [getFeedConnectionsHandler]
  );

  const handleBackToFeeds = useCallback(() => {
    setShowConnections(false);
    setSelectedFeed(null);
    setFeedConnections([]);
    setSelectedConnections([]);
    setIsAllSelected(false);

    // Remove connections state from localStorage but keep showManageFeeds
    localStorage.removeItem("postsSection_showConnections");
    localStorage.removeItem("postsSection_selectedFeedId");
  }, []);

  const handleConnectionSelect = useCallback((connectionId: string) => {
    setSelectedConnections((prev) =>
      prev.includes(connectionId)
        ? prev.filter((id) => id !== connectionId)
        : [...prev, connectionId]
    );
  }, []);

  const selectAllCheckboxHandler = useCallback(() => {
    const selectedIds = !isAllSelected
      ? feedConnections.map((conn) => conn._id)
      : [];
    setIsAllSelected(!isAllSelected);
    setSelectedConnections(selectedIds);
  }, [isAllSelected, feedConnections]);

  const selectOnThisPageHandler = useCallback(() => {
    const newConnections = feedConnections.filter(
      (connection) => !selectedConnections.includes(connection._id)
    );
    setIsAllSelected(false);
    setSelectedConnections([
      ...selectedConnections,
      ...newConnections.map((conn) => conn._id),
    ]);
  }, [feedConnections, selectedConnections]);

  const clearHandler = useCallback(() => {
    setIsAllSelected(false);
    setSelectedConnections([]);
  }, []);

  const handleRemoveFromFeed = useCallback(() => {
    if (!selectedFeed || selectedConnections.length === 0) return;

    setDestructiveModal({
      headingText: "Remove from feed",
      descriptionText: `Are you sure you want to remove ${
        selectedConnections.length
      } connection${selectedConnections.length > 1 ? "s" : ""} from this feed?`,
      confirmBtnTitle: "Remove",
      onCancelHandler: () => hideModal(),
      onConfirmHandler: async () => {
        hideModal();
        try {
          const result = await bulkRemoveConnectionsFromFeedHandler(
            selectedFeed._id,
            selectedConnections
          );
          if (result?.success) {
            // Refresh feed connections
            const updatedResult = await getFeedConnectionsHandler(
              selectedFeed._id
            );
            setFeedConnections(updatedResult?.connections || []);
            setSelectedConnections([]);
          }
        } catch (error) {
          console.error("Error removing connections from feed:", error);
        }
      },
    });
  }, [
    selectedConnections,
    selectedFeed,
    setDestructiveModal,
    hideModal,
    bulkRemoveConnectionsFromFeedHandler,
    getFeedConnectionsHandler,
  ]);

  const handleRemoveSingleConnectionFromFeed = useCallback(
    (connectionId: string) => {
      if (!selectedFeed) return;

      const connection = feedConnections.find(
        (conn) => conn._id === connectionId
      );
      const connectionName = connection
        ? `${connection.firstName} ${connection.lastName}`
        : "this connection";

      setDestructiveModal({
        headingText: "Remove from feed",
        descriptionText: `Are you sure you want to remove ${connectionName} from this feed?`,
        onCancelHandler: () => hideModal(),
        confirmBtnTitle: "Remove",
        onConfirmHandler: async () => {
          hideModal();
          try {
            const result = await bulkRemoveConnectionsFromFeedHandler(
              selectedFeed._id,
              [connectionId]
            );
            if (result?.success) {
              // Refresh feed connections
              const updatedResult = await getFeedConnectionsHandler(
                selectedFeed._id
              );
              setFeedConnections(updatedResult?.connections || []);
            }
          } catch (error) {
            console.error("Error removing connection from feed:", error);
          }
        },
      });
    },
    [
      selectedFeed,
      feedConnections,
      setDestructiveModal,
      hideModal,
      bulkRemoveConnectionsFromFeedHandler,
      getFeedConnectionsHandler,
    ]
  );

  const handleDelete = useCallback(
    (feedId: string) => {
      setDestructiveModal({
        headingText: "Delete feed",
        descriptionText: "Are you sure you want to delete this feed?",
        onCancelHandler: () => hideModal(),
        onConfirmHandler: () => {
          hideModal();
          deleteFeedHandler(feedId);
        },
      });
    },
    [setDestructiveModal, hideModal, deleteFeedHandler]
  );

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };

  const sortHandler = (props: any) => {
    const adjustedColumns = adjustedColumnsHandler(
      columnsInitialState,
      columns,
      tableRef,
      isSmall,
      isMedium,
      sortHandler
    );
    const selectedColumnIndex = columns.findIndex(
      (column) => column.key === props.key
    );
    if (selectedColumnIndex !== -1) {
      props.sort = props.sort === "" || props.sort === "za" ? "az" : "za";
      adjustedColumns[selectedColumnIndex] = props;
      setColumns(adjustedColumns);
    }
  };

  const columnsInitialState = [
    {
      header: "Name",
      key: ["name"],
      show: true,
      sort: "",
      sortHandler: (e: any) => sortHandler(e),
      showSmall: true,
    },
    {
      header: "Contacts",
      key: ["numConnections"],
      show: true,
      showSmall: true,
    },
    { header: "Date Created", key: ["createdAt"], show: true, showSmall: true },
    { header: "Actions", key: ["actions"], show: true, showSmall: true },
  ];

  const connectionsColumnsInitialState = [
    {
      header: "Name",
      key: ["name"],
      show: true,
      sort: "",
      sortHandler: (e: any) => sortHandler(e),
      showSmall: true,
      width: "250px",
    },
    {
      header: "Email",
      key: ["email"],
      show: true,
      showSmall: true,
      width: "300px",
    },
    { header: "Date Added", key: ["dateAdded"], show: true, showSmall: true },
    { header: "Actions", key: ["actions"], show: true, showSmall: true },
  ];

  const [columns, setColumns] = useState(columnsInitialState);
  const [connectionsColumns, setConnectionsColumns] = useState(
    connectionsColumnsInitialState
  );

  useEffect(() => {
    const adjustedColumns = adjustedColumnsHandler(
      columnsInitialState,
      columns,
      tableRef,
      isSmall,
      isMedium,
      sortHandler
    );
    setColumns(adjustedColumns);
  }, [isSmall, isMedium]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const adjustedConnectionsColumns = adjustedColumnsHandler(
      connectionsColumnsInitialState,
      connectionsColumns,
      tableRef,
      isSmall,
      isMedium,
      sortHandler
    );

    // Add renderHeaderCell to the first column (Name column)
    if (adjustedConnectionsColumns.length > 0) {
      adjustedConnectionsColumns[0].renderHeaderCell = () => (
        <Row gap="8px" alignItems>
          <SelectAllCheckbox
            isCheckboxChecked={
              isAllSelected ||
              (selectedConnections.length > 0 &&
                selectedConnections.length === feedConnections.length)
            }
            pageSize={feedConnections.length}
            total={feedConnections.length}
            paginated={feedConnections.length}
            isAllSelected={isAllSelected}
            selected={selectedConnections.length}
            label={"contacts"}
            selectAllHandler={selectAllCheckboxHandler}
            selectOnThisPageHadler={selectOnThisPageHandler}
            clearHandler={clearHandler}
            backgroundColor="white"
          />
          {selectedConnections.length === 0 && (
            <Text $lighter $heading6>
              Name
            </Text>
          )}
        </Row>
      );
    }

    setConnectionsColumns(adjustedConnectionsColumns);
  }, [
    isSmall,
    isMedium,
    isAllSelected,
    selectedConnections,
    feedConnections,
    selectAllCheckboxHandler,
    selectOnThisPageHandler,
    clearHandler,
  ]); // eslint-disable-line react-hooks/exhaustive-deps

  if (showConnections) {
    return (
      <Container>
        <ContentContainer>
          <Column gap="8px">
            <HeaderSection>
              <BackSection>
                <ActionText icon="arrowLeft" onClickHandler={handleBackToFeeds}>
                  Back to Manage feeds
                </ActionText>
              </BackSection>
              <TitleSection>
                <Row gap="12px">
                  <Column gap="12px" spaceBetween>
                    <Text $heading4>{selectedFeed?.name}</Text>
                  </Column>
                </Row>
              </TitleSection>
            </HeaderSection>
            <ActionSection>
              <Row gap="8px">
               <Button
                  $type="light-blue"
                  $color={styles.colors.black600}
                  $width="135px"
                  onClickHandler={handleRemoveFromFeed}
                  disabled={selectedConnections.length === 0}
                >
                  Remove from feed
                </Button>
                <Button
                  $type="blue"
                  $width="163px"
                  $color={styles.colors.white}
                  onClickHandler={() => {
                    if (selectedFeed) {
                      setCreateFeedModal([], false, async () => {
                        // Refresh feed connections after adding URLs
                        setConnectionsLoading(true);
                        try {
                          const result = await getFeedConnectionsHandler(selectedFeed._id);
                          setFeedConnections(result?.connections || []);
                        } catch (error) {
                          console.error("Error refreshing feed connections:", error);
                        } finally {
                          setConnectionsLoading(false);
                        }
                      }, true, selectedFeed._id);
                    }
                  }}
                >
                  <Row alignItems gap="8px">
                    <IconWrapper>
                      <SvgFragment type="plus" />
                    </IconWrapper>
                    Add LinkedIn URLs
                  </Row>
                </Button>
              </Row>
            </ActionSection>
            <TableContainer>
              <Table
                ref={tableRef}
                pageLoading={connectionsLoading ? 10 : undefined}
                noDataPage={
                  <EmptyPage
                    svgType="feedEmptyState"
                    title="No contacts in this feed"
                    label="Add contacts and start engaging"
                  />
                }
                columns={connectionsColumns}
                data={(feedConnections || []).map(
                  (connection: IConnection) => ({
                    object: { ...connection, borderTop: "#e0e0e0 1px solid" },
                    name: (
                      <Row gap="8px" alignItems>
                        <Checkbox
                          checked={selectedConnections.includes(connection._id)}
                          onClickHandler={() =>
                            handleConnectionSelect(connection._id)
                          }
                          checkboxId={connection._id}
                        />
                        <ProfilePhoto
                          tooltipMessage={`${connection.firstName} ${connection.lastName}`}
                          capitalLetters={capitalLetters(connection)}
                          source={connection.profilePicture}
                        />
                        <Text
                          $bold
                        >{`${connection.firstName} ${connection.lastName}`}</Text>
                      </Row>
                    ),
                    email: (
                      <Text $ellipsis="0" showTooltip>
                        {connection.email || ""}
                      </Text>
                    ),
                    dateAdded: (
                      <Text>
                        {(() => {
                          const feedData = connection.feeds?.find(
                            (feed) => feed._id === selectedFeed?._id
                          );
                          return feedData?.dateAdded
                            ? formatDate(feedData.dateAdded)
                            : "";
                        })()}
                      </Text>
                    ),
                    actions: (
                      <div
                        style={{ display: "flex", justifyContent: "flex-end" }}
                      >
                        <ConnectionActionWithFeedsDropdown
                          connection={connection}
                          currentFeedId={selectedFeed?._id}
                          onUpdate={async () => {
                            // Refresh feed connections when feeds are updated
                            const updatedResult =
                              await getFeedConnectionsHandler(
                                selectedFeed!._id
                              );
                            setFeedConnections(
                              updatedResult?.connections || []
                            );
                          }}
                          onCreateFeed={(connectionId) => {
                            // Open create feed modal with selected connection
                            setCreateFeedModal([connectionId], true);
                          }}
                        />
                      </div>
                    ),
                  })
                )}
              />
            </TableContainer>
          </Column>
        </ContentContainer>
      </Container>
    );
  }

  return (
    <Container>
      <ContentContainer>
        <Column gap="24px">
          <HeaderSection>
            <BackSection>
              <ActionText icon="arrowLeft" onClickHandler={() => onBack()}>
                Back to feed
              </ActionText>
            </BackSection>
            <TitleSection>
              <Row gap="12px">
                <Column gap="12px" spaceBetween>
                  <Text $heading4>Manage feeds</Text>
                  <Text $paragraph4>
                    Create feeds based on people you want to engage with.
                    Comment and react when they post to LinkedIn. We suggest
                    creating separate feeds for categories such as target
                    prospects, existing customers, competitors, and influencers.
                  </Text>
                </Column>
              </Row>
            </TitleSection>
          </HeaderSection>

          <TableContainer>
            <Table
              ref={tableRef}
              pageLoading={loading ? 10 : undefined}
              noDataPage={
                <EmptyPage
                  svgType="feedEmptyState"
                  title="No feeds created yet"
                  label="Create your first feed, add contacts whose posts you want to track and start engaging"
                />
              }
              columns={columns}
              $noGap={true}
              data={(feeds || []).map((feed: IFeed) => ({
                object: { ...feed, borderTop: "#e0e0e0 1px solid" },
                onRowClick: (feed: IFeed) => handleFeedClick(feed),
                name: <Text>{feed.name}</Text>,
                numConnections: <Text>{feed.numConnections || 0}</Text>,
                createdAt: <Text>{formatDate(feed.createdAt)}</Text>,
                actions: (
                  <div
                    style={{ display: "flex", justifyContent: "flex-end" }}
                    onClick={(e: any) => e.stopPropagation()}
                  >
                    <EditDeleteActionDropdown
                      object={feed}
                      type="feed"
                      title="Rename"
                      deleteTitle="Delete"
                      onDeleteHandler={() => handleDelete(feed._id)}
                      onEditHandler={() => handleRename(feed)}
                      customActions={[
                        {
                          icon: 'linkedin',
                          title: 'Add LinkedIn URLs',
                          onClickHandler: (feed: IFeed) => {
                            setCreateFeedModal([], false, async () => {
                              // Refresh feed connections after adding URLs
                              if (showConnections && selectedFeed?._id === feed._id) {
                                setConnectionsLoading(true);
                                try {
                                  const result = await getFeedConnectionsHandler(feed._id);
                                  setFeedConnections(result?.connections || []);
                                } catch (error) {
                                  console.error("Error refreshing feed connections:", error);
                                } finally {
                                  setConnectionsLoading(false);
                                }
                              }
                            }, true, feed._id);
                          }
                        }
                      ]}
                    />
                  </div>
                ),
              }))}
            />
          </TableContainer>
        </Column>
      </ContentContainer>
    </Container>
  );
};

const Container = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  padding: 24px;
`;

const ContentContainer = styled.div`
  width: 100%;
  max-width: 800px;
  margin: 0 auto;
  padding: 32px 24px;
  background: white;
  border-radius: 4px;
`;

const HeaderSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const BackSection = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;

  span {
    color: ${styles.colors.black400};
  }

  svg {
    path {
      fill: ${styles.colors.primary600};
    }
  }
`;

const TitleSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const TableContainer = styled.div`
  width: 100%;
`;

const ActionSection = styled.div`
  display: flex;
  justify-content: flex-end;
`;

const IconWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  
  svg {
    width: 10px;
    height: 10px;
  }
`;

export default ManageFeeds;
